import { Calendar } from "@msuite/ui/calendar";
import { ChartHeader } from "@msuite/ui/charts";
import { DashboardTile } from "@msuite/ui/dashboard";
import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { formatNumber, guardNumber } from "@msuite/utils/numbers";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useUserAbsences } from "@/hooks/use-user-absences";
import { AddAbsenceModal } from "@/modals/users/add-absence-modal";
import type { DashboardTileProps } from "../shared";
import { DashboardAbsenceItem } from "./dashboard-absence-item";

export function DashboardAbsencesItem({ item }: DashboardTileProps) {
	/** Hooks */
	const { t } = useTranslation();
	const controls = Calendar.useControls();
	const auth = useAuth();

	const disclosure = useDisclosure();

	const {
		data: balance,
		isLoading,
		queryKey,
	} = useQuery({
		queryKey: ["dashboard", "absences", controls.current.format("YYYY")],
		queryFn: async () => {
			const userId = auth.user?.id;
			if (!userId) throw new Error();
			const response = await client.users[":userId"].absences.balance.$get({
				param: { userId },
				query: { year: controls.current.year().toString() },
			});
			const data = await responseHandler(response);
			return data.balance;
		},
	});

	const { absences } = useUserAbsences(guardString(auth.user?.id), {}, controls.current.year());

	/** Render */
	return (
		<React.Fragment>
			<DashboardTile item={item}>
				<ChartHeader
					title={t("dashboard.absences")}
					description={`${controls.current.format("YYYY")} - ${t("absences.past")}: ${formatNumber(guardNumber(balance?.past), "day")} - ${t("absences.upcoming")}: ${formatNumber(guardNumber(balance?.upcoming), "day")}`}
					controls={
						<DashboardTile.Controls
							calendarControls={controls}
							onAdd={disclosure.onOpen}
							queryKey={queryKey}
						/>
					}
				/>
				<DashboardTile.List
					queryKey={queryKey}
					isLoading={isLoading}
					data={absences}
				>
					{guardArray(absences).map((absence) => (
						<DashboardAbsenceItem
							key={absence.id}
							absence={absence}
						/>
					))}
				</DashboardTile.List>
			</DashboardTile>
			{disclosure.isOpen && (
				<AddAbsenceModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
				/>
			)}
		</React.Fragment>
	);
}
